import { UserRole } from '@/shared/types';

/**
 * GH#1641 — a qué ruta lleva el click en una notificación del Header.
 *
 * Antes navegaba a rutas SIN el prefijo del rol (`/leads/:id`, `/clientes/:id`, …)
 * que no están declaradas → 404. El Header se monta en el MainLayout compartido
 * (envolverRuta, en App.tsx, envuelve TODA ruta con requiresLayout), así que lo ve cualquier
 * rol; por eso el destino se resuelve por rol → prefijo.
 *
 * Regla (CTO): la notificación no es una orden de editar → a la LISTA, salvo el
 * lead que sí tiene detalle. 'sale' no tiene ruta declarada → sin navegación.
 * Para prefijos/tipos sin ruta declarada, tampoco se navega (no inventar destinos).
 */

export type NotifEntityType = 'lead' | 'property' | 'client' | 'quote' | 'appointment' | 'sale';

const PREFIJO_POR_ROL: Partial<Record<UserRole, string>> = {
  [UserRole.AGENCY_ADMIN]: '/agencia',
  [UserRole.AGENCY_AGENT_USER]: '/agente',
  [UserRole.PROJECT_DEVELOPER_ADMIN]: '/promotora',
  [UserRole.ACCOUNTING_TEAM]: '/accounting',
  [UserRole.OPERATIONAL_TEAM]: '/operational',
  [UserRole.CONDOMINIUM_ADMIN]: '/condominium',
  [UserRole.LEGAL_MODULE]: '/legal',
  [UserRole.SUPER_ADMIN]: '/admin',
};

/**
 * Templates de ruta por PREFIJO. Hoy solo el prefijo /agencia tiene rutas
 * declaradas para estas entidades (verificado contra agencyRoutes). Para otros
 * prefijos no se navega hasta confirmar que existen equivalentes.
 * El test recorre este mapa y verifica que cada destino exista en el router.
 */
export const RUTAS_NOTIF_POR_PREFIJO: Record<string, Partial<Record<NotifEntityType, string>>> = {
  '/agencia': {
    lead: '/agencia/crm/leads/:id', // detalle
    property: '/agencia/propiedades', // lista (la notif no es orden de editar)
    client: '/agencia/clientes', // lista
    quote: '/agencia/cotizaciones', // lista
    appointment: '/agencia/agenda', // calendario
    // sale: sin ruta declarada (/agencia/ventas no existe) → sin navegación.
  },
};

/**
 * Devuelve la ruta a la que navegar, o null si no hay destino (no navegar y solo
 * marcar la notificación como leída).
 */
export function rutaDeNotificacion(
  entityType: string,
  entityId: string,
  role?: UserRole | null,
): string | null {
  const prefijo = role ? PREFIJO_POR_ROL[role] : undefined;
  if (!prefijo) return null;
  const mapa = RUTAS_NOTIF_POR_PREFIJO[prefijo];
  if (!mapa) return null;
  const template = mapa[entityType as NotifEntityType];
  if (!template) return null;
  return template.replace(':id', entityId);
}
